import React from 'react';

type AdminTableCellValue = string | number | boolean | Date | null | undefined;
type AdminTableSortValue = string | number | boolean | Date | null | undefined;
export type AdminDataTableRowId = string | number;
export type AdminDataTableSortDirection = 'ascending' | 'descending';
export type AdminDataTableSortState = {
  columnId: string;
  direction: AdminDataTableSortDirection;
} | null;

export type AdminDataTableFilter =
  | { type: 'text'; placeholder?: string }
  | {
      type: 'select';
      options: ReadonlyArray<{ value: string; label: string }>;
      allLabel?: string;
    };

export interface AdminDataTableColumn<Row> {
  id: string;
  header: React.ReactNode;
  headerAccessory?: React.ReactNode;
  ariaLabel?: string;
  accessor: (row: Row) => AdminTableCellValue;
  render?: (row: Row, value: AdminTableCellValue) => React.ReactNode;
  filterValue?: (row: Row) => AdminTableCellValue;
  sortValue?: (row: Row) => AdminTableSortValue;
  filter?: AdminDataTableFilter;
  sortable?: boolean;
  numeric?: boolean;
  width?: string | number;
  className?: string;
}

export type AdminDataTableFilterValues = Record<string, string>;

export interface AdminDataTableBulkContext<Row> {
  selectedRows: readonly Row[];
  clearSelection: () => void;
  deselectIds: (ids: readonly AdminDataTableRowId[]) => void;
}

export interface AdminDataTableProps<Row> {
  ariaLabel: string;
  rows: readonly Row[];
  columns: readonly AdminDataTableColumn<Row>[];
  getRowId: (row: Row) => AdminDataTableRowId;
  getRowLabel?: (row: Row) => string;
  selectable?: boolean;
  onRowActivate?: (row: Row) => void;
  bulkSlot?: (context: AdminDataTableBulkContext<Row>) => React.ReactNode;
  emptyState?: React.ReactNode;
  filterValues?: AdminDataTableFilterValues;
  onFilterValuesChange?: (filters: AdminDataTableFilterValues) => void;
  sort?: AdminDataTableSortState;
  onSortChange?: (sort: AdminDataTableSortState) => void;
  selectedIds?: readonly AdminDataTableRowId[];
  onSelectedIdsChange?: (ids: readonly AdminDataTableRowId[]) => void;
  className?: string;
}

const getColumnLabel = <Row,>(column: AdminDataTableColumn<Row>): string =>
  column.ariaLabel ?? (typeof column.header === 'string' ? column.header : column.id);

const getCellText = (value: AdminTableCellValue): string => {
  if (value === null || value === undefined) {
    return '';
  }

  return value instanceof Date ? value.toISOString() : String(value);
};

const compareTableValues = (left: AdminTableSortValue, right: AdminTableSortValue): number => {
  const a = left instanceof Date ? left.getTime() : left;
  const b = right instanceof Date ? right.getTime() : right;
  if (a === b) {
    return 0;
  }
  if (a === null || a === undefined) {
    return 1;
  }
  if (b === null || b === undefined) {
    return -1;
  }
  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
};

const filterRows = <Row,>(
  rows: readonly Row[],
  columns: readonly AdminDataTableColumn<Row>[],
  filters: AdminDataTableFilterValues
): Row[] => rows.filter((row) => columns.every((column) => {
  const query = filters[column.id] ?? '';
  if (!column.filter || query === '') {
    return true;
  }
  const value = column.filterValue?.(row) ?? column.accessor(row);
  const normalizedValue = getCellText(value).toLocaleLowerCase();
  const normalizedQuery = query.toLocaleLowerCase();
  return column.filter.type === 'select'
    ? normalizedValue === normalizedQuery
    : normalizedValue.includes(normalizedQuery);
}));

const sortRows = <Row,>(
  rows: readonly Row[],
  columns: readonly AdminDataTableColumn<Row>[],
  sort: AdminDataTableSortState
): Row[] => {
  if (sort === null) {
    return [...rows];
  }
  const column = columns.find((candidate) => candidate.id === sort.columnId);
  if (!column) {
    return [...rows];
  }
  const multiplier = sort.direction === 'ascending' ? 1 : -1;
  return [...rows].sort((left, right) => compareTableValues(
    column.sortValue?.(left) ?? column.accessor(left),
    column.sortValue?.(right) ?? column.accessor(right)
  ) * multiplier);
};

const getRowLabel = <Row,>(row: Row, columns: readonly AdminDataTableColumn<Row>[]): string => {
  const value = columns.map((column) => getCellText(column.accessor(row))).find(Boolean);
  return value ?? 'row';
};

const getGridTemplate = <Row,>(
  columns: readonly AdminDataTableColumn<Row>[],
  selectable: boolean
): string => [
  ...(selectable ? ['36px'] : []),
  ...columns.map((column) => typeof column.width === 'number'
    ? `${column.width}px`
    : column.width ?? 'minmax(0, 1fr)'),
].join(' ');

const getColumnMinimum = <Row,>(column: AdminDataTableColumn<Row>): number => {
  if (typeof column.width === 'number') {
    return column.width;
  }
  const width = column.width?.trim();
  if (width?.endsWith('px')) {
    return Number.parseFloat(width) || 120;
  }
  if (width?.endsWith('fr')) {
    return Math.max((Number.parseFloat(width) || 1) * 120, 96);
  }
  return 120;
};

const getTableMinimum = <Row,>(
  columns: readonly AdminDataTableColumn<Row>[],
  selectable: boolean
): number => {
  const gaps = Math.max(columns.length + (selectable ? 1 : 0) - 1, 0) * 14;
  const selection = selectable ? 36 : 0;
  return columns.reduce((total, column) => total + getColumnMinimum(column), selection) + gaps;
};

const useAdminTableModel = <Row,>(props: AdminDataTableProps<Row>) => {
  const rows = props.rows;
  const getRowId = props.getRowId;
  const [internalFilters, setInternalFilters] = React.useState<AdminDataTableFilterValues>({});
  const [internalSort, setInternalSort] = React.useState<AdminDataTableSortState>(null);
  const [internalSelectedIds, setInternalSelectedIds] = React.useState<Set<AdminDataTableRowId>>(
    () => new Set()
  );
  const filters = props.filterValues ?? internalFilters;
  const sort = props.sort === undefined ? internalSort : props.sort;
  const selectedIds = React.useMemo(
    () => props.selectedIds === undefined ? internalSelectedIds : new Set(props.selectedIds),
    [internalSelectedIds, props.selectedIds]
  );
  const selectedIdsRef = React.useRef(selectedIds);
  selectedIdsRef.current = selectedIds;
  const filteredRows = React.useMemo(
    () => filterRows(props.rows, props.columns, filters),
    [props.rows, props.columns, filters]
  );
  const visibleRows = React.useMemo(
    () => sortRows(filteredRows, props.columns, sort),
    [filteredRows, props.columns, sort]
  );
  const selectedRows = React.useMemo(
    () => rows.filter((row) => selectedIds.has(getRowId(row))),
    [rows, getRowId, selectedIds]
  );
  const visibleIds = visibleRows.map(getRowId);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someVisibleSelected = visibleIds.some((id) => selectedIds.has(id));

  const setFilter = (columnId: string, value: string) => {
    const next = { ...filters, [columnId]: value };
    if (props.filterValues === undefined) {
      setInternalFilters(next);
    }
    props.onFilterValuesChange?.(next);
  };
  const cycleSort = (columnId: string) => {
    const next = (() => {
      const current = sort;
      if (current?.columnId !== columnId) {
        return { columnId, direction: 'ascending' as const };
      }
      return current.direction === 'ascending'
        ? { columnId, direction: 'descending' as const }
        : null;
    })();
    if (props.sort === undefined) {
      setInternalSort(next);
    }
    props.onSortChange?.(next);
  };
  const commitSelection = (next: Set<AdminDataTableRowId>) => {
    selectedIdsRef.current = next;
    if (props.selectedIds === undefined) {
      setInternalSelectedIds(next);
    }
    props.onSelectedIdsChange?.([...next]);
  };
  const toggleRow = (id: AdminDataTableRowId) => {
    const next = new Set(selectedIdsRef.current);
    next.has(id) ? next.delete(id) : next.add(id);
    commitSelection(next);
  };
  const toggleAll = () => {
    const next = new Set(selectedIdsRef.current);
    visibleIds.forEach((id) => allVisibleSelected ? next.delete(id) : next.add(id));
    commitSelection(next);
  };
  const deselectIds = (ids: readonly AdminDataTableRowId[]) => {
    const next = new Set(selectedIdsRef.current);
    ids.forEach((id) => next.delete(id));
    commitSelection(next);
  };
  const clearSelection = () => commitSelection(new Set());

  return {
    filters, sort, selectedIds, selectedRows, visibleRows, allVisibleSelected,
    someVisibleSelected, setFilter, cycleSort, toggleRow, toggleAll, clearSelection,
    deselectIds,
  };
};

interface SelectionCheckboxProps {
  label: string;
  checked: boolean;
  indeterminate?: boolean;
  onChange: () => void;
}

const SelectionCheckbox: React.FC<SelectionCheckboxProps> = ({
  label,
  checked,
  indeterminate = false,
  onChange,
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <input
      ref={inputRef}
      className="admin-table-checkbox"
      type="checkbox"
      aria-label={label}
      checked={checked}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
      onChange={onChange}
    />
  );
};

interface HeaderRowProps<Row> {
  columns: readonly AdminDataTableColumn<Row>[];
  selectable: boolean;
  gridTemplate: string;
  filters: AdminDataTableFilterValues;
  sort: AdminDataTableSortState;
  allSelected: boolean;
  someSelected: boolean;
  onFilter: (columnId: string, value: string) => void;
  onSort: (columnId: string) => void;
  onToggleAll: () => void;
}

const ColumnFilter = <Row,>({
  column,
  value,
  onChange,
}: {
  column: AdminDataTableColumn<Row>;
  value: string;
  onChange: (value: string) => void;
}) => {
  const label = `Filter ${getColumnLabel(column)}`;
  if (column.filter?.type === 'text') {
    return <input className="admin-table-filter" aria-label={label} placeholder={column.filter.placeholder} value={value} onChange={(event) => onChange(event.target.value)} />;
  }
  if (column.filter?.type === 'select') {
    return (
      <select className="admin-table-filter" aria-label={label} value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">{column.filter.allLabel ?? 'All'}</option>
        {column.filter.options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
    );
  }
  return <span className="admin-table-filter-spacer" aria-hidden="true" />;
};

const HeaderRow = <Row,>(props: HeaderRowProps<Row>) => (
  <div className="admin-table-header" role="row" style={{ gridTemplateColumns: props.gridTemplate }}>
    {props.selectable && (
      <div className="admin-table-column admin-table-column--selection" role="columnheader">
        <SelectionCheckbox
          label="Select all filtered rows"
          checked={props.allSelected}
          indeterminate={!props.allSelected && props.someSelected}
          onChange={props.onToggleAll}
        />
      </div>
    )}
    {props.columns.map((column) => {
      const label = getColumnLabel(column);
      const activeSort = props.sort?.columnId === column.id ? props.sort.direction : 'none';
      return (
        <div key={column.id} className={`admin-table-column${column.numeric ? ' admin-table-column--numeric' : ''}`} role="columnheader" aria-sort={activeSort}>
          <div className="admin-table-heading-row">
            {column.sortable === false ? <span className="admin-table-heading">{column.header}</span> : (
              <button className="admin-table-sort" type="button" aria-label={`Sort ${label}`} onClick={() => props.onSort(column.id)}>
                <span>{column.header}</span>
                <span className="admin-table-sort__indicator" aria-hidden="true">{activeSort === 'ascending' ? '\u25B2' : activeSort === 'descending' ? '\u25BC' : ''}</span>
              </button>
            )}
            {column.headerAccessory}
          </div>
          <ColumnFilter column={column} value={props.filters[column.id] ?? ''} onChange={(value) => props.onFilter(column.id, value)} />
        </div>
      );
    })}
  </div>
);

interface DataRowProps<Row> {
  row: Row;
  columns: readonly AdminDataTableColumn<Row>[];
  rowId: AdminDataTableRowId;
  label: string;
  selectable: boolean;
  selected: boolean;
  gridTemplate: string;
  onToggle: () => void;
  onActivate?: (row: Row) => void;
}

const isInteractiveTarget = (target: EventTarget | null, currentTarget: Element): boolean =>
  target instanceof Element && target !== currentTarget && Boolean(target.closest('button, a, input, select, textarea, [role="button"]'));

const DataRow = <Row,>(props: DataRowProps<Row>) => {
  const activate = (event: React.MouseEvent | React.KeyboardEvent) => {
    if (!props.onActivate || isInteractiveTarget(event.target, event.currentTarget)) {
      return;
    }
    if ('key' in event && event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    if ('key' in event) {
      event.preventDefault();
    }
    props.onActivate(props.row);
  };

  return (
    <div
      className={`admin-table-row${props.selected ? ' admin-table-row--selected' : ''}${props.onActivate ? ' admin-table-row--interactive' : ''}`}
      role="row"
      tabIndex={props.onActivate ? 0 : undefined}
      style={{ gridTemplateColumns: props.gridTemplate }}
      onClick={activate}
      onKeyDown={activate}
    >
      {props.selectable && <div className="admin-table-cell admin-table-cell--selection" role="cell"><SelectionCheckbox label={`Select ${props.label}`} checked={props.selected} onChange={props.onToggle} /></div>}
      {props.columns.map((column) => {
        const value = column.accessor(props.row);
        return (
          <div
            key={column.id}
            className={`admin-table-cell${column.numeric ? ' admin-table-cell--numeric' : ''}${column.className ? ` ${column.className}` : ''}`}
            role="cell"
            style={column.numeric ? { fontVariantNumeric: 'tabular-nums' } : undefined}
          >
            {column.render ? column.render(props.row, value) : getCellText(value)}
          </div>
        );
      })}
    </div>
  );
};

export const AdminDataTable = <Row,>(props: AdminDataTableProps<Row>) => {
  const selectable = props.selectable ?? false;
  const model = useAdminTableModel(props);
  const gridTemplate = getGridTemplate(props.columns, selectable);
  const tableMinimum = getTableMinimum(props.columns, selectable);

  return (
    <div className={`admin-data-table ${props.className ?? ''}`.trim()}>
      {selectable && model.selectedRows.length > 0 && (
        <div className="admin-table-bulk">
          {props.bulkSlot?.({
            selectedRows: model.selectedRows,
            clearSelection: model.clearSelection,
            deselectIds: model.deselectIds,
          })}
          <button className="admin-table-clear" type="button" onClick={model.clearSelection}>Clear</button>
        </div>
      )}
      <div
        aria-label={`${props.ariaLabel} table scroll area`}
        className="admin-table-scroll"
        role="region"
        tabIndex={0}
      >
        <div
          aria-label={props.ariaLabel}
          aria-rowcount={model.visibleRows.length + 1}
          className="admin-table-scroll__content"
          role="table"
          style={{ minWidth: tableMinimum }}
        >
          <div role="rowgroup">
            <HeaderRow
              columns={props.columns}
              selectable={selectable}
              gridTemplate={gridTemplate}
              filters={model.filters}
              sort={model.sort}
              allSelected={model.allVisibleSelected}
              someSelected={model.someVisibleSelected}
              onFilter={model.setFilter}
              onSort={model.cycleSort}
              onToggleAll={model.toggleAll}
            />
          </div>
          <div role="rowgroup">
            {model.visibleRows.length === 0 ? (
              <div className="admin-table-empty-row" role="row" style={{ gridTemplateColumns: gridTemplate }}>
                <div className="admin-table-empty" role="cell">{props.emptyState ?? 'Nothing matches the current filters'}</div>
              </div>
            ) : model.visibleRows.map((row) => {
              const rowId = props.getRowId(row);
              const label = props.getRowLabel?.(row) ?? getRowLabel(row, props.columns);
              return <DataRow key={rowId} row={row} columns={props.columns} rowId={rowId} label={label} selectable={selectable} selected={model.selectedIds.has(rowId)} gridTemplate={gridTemplate} onToggle={() => model.toggleRow(rowId)} onActivate={props.onRowActivate} />;
            })}
          </div>
        </div>
      </div>
      <div
        aria-live="polite"
        className="admin-table-footer"
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {model.visibleRows.length} of {props.rows.length} shown
      </div>
    </div>
  );
};
